<template>
    <div>
        <div v-if="inputLimitError || fileError" role="alert" class="mb-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 text-xs text-red-800">
            {{ inputLimitError || fileError }}
        </div>
        <!-- File preview strip -->
        <div v-if="selectedFiles.length > 0" class="flex flex-wrap gap-2 mb-2">
            <div v-for="(file, index) in selectedFiles" :key="index" class="relative group">
                <img v-if="file.mediaType.startsWith('image/') && file.url" :src="file.url" class="h-16 w-16 object-cover rounded border border-slate-200" />
                <div v-else class="h-16 px-3 flex items-center gap-1.5 bg-slate-50 rounded border border-slate-200 text-xs text-slate-600">
                    <UIcon name="i-heroicons-document" class="w-4 h-4 flex-shrink-0" />
                    <span class="max-w-[80px] truncate">{{ file.filename }}</span>
                </div>
                <button type="button" @click="removeFile(index)" class="absolute -top-1.5 -right-1.5 w-4 h-4 bg-slate-600 text-white rounded-full flex items-center justify-center text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity">&times;</button>
            </div>
        </div>
        <input type="file" ref="fileInputRef" class="hidden" :accept="ACCEPTED_EXTENSIONS" multiple @change="onFilesSelected" />
        <UChatPrompt
            v-model="textModel"
            :placeholder="placeholder"
            variant="outline"
            :size="size"
            :rows="rows"
            :maxrows="maxrows"
            autoresize
            :autofocus="autofocus"
            :disabled="disabled"
            ref="promptRef"
            @submit="onSubmit"
        >
            <template #footer>
                <div class="flex items-center justify-between w-full">
                    <div class="flex items-center gap-1">
                        <UButton type="button" size="xs" color="neutral" variant="ghost" icon="i-heroicons-paper-clip" :disabled="disabled" @click="openFilePicker" />
                        <UTooltip :text="voiceTooltip">
                            <span class="relative inline-flex">
                                <UButton
                                    type="button"
                                    size="xs"
                                    :color="isRecording ? 'error' : 'neutral'"
                                    :variant="isRecording ? 'solid' : 'ghost'"
                                    :icon="isTranscribing && !isRecording ? 'i-heroicons-arrow-path' : 'i-heroicons-microphone'"
                                    :disabled="disabled || (isTranscribing && !isRecording)"
                                    :class="[isTranscribing && !isRecording && 'animate-spin', isRecording && 'animate-pulse']"
                                    @mousedown.prevent="onVoiceStart"
                                    @touchstart.prevent="onVoiceStart"
                                />
                                <span
                                    v-if="!voiceDiscovered"
                                    class="absolute -top-1 -right-1 flex h-2.5 w-2.5"
                                >
                                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
                                    <span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary-500" />
                                </span>
                            </span>
                        </UTooltip>
                        <span v-if="isRecording" class="text-xs text-red-500 font-medium">Listening...</span>
                        <span v-else-if="isTranscribing" class="text-xs text-blue-500">Finishing up...</span>
                        <span v-else-if="voiceError" class="text-xs text-red-500">{{ voiceError }}</span>
                        <span v-else-if="!voiceDiscovered" class="text-xs text-primary-500">
                            Try voice input — press <kbd class="px-1 py-0.5 bg-primary-50 rounded text-[10px] font-mono">{{ isMac ? '⌥' : 'Alt' }}+V</kbd> or click <UIcon name="i-heroicons-microphone" class="inline w-3 h-3 align-text-bottom" />
                        </span>
                        <span v-else class="text-xs text-slate-400">{{ loading && !queued ? 'Press Enter to queue' : 'Press Enter to send' }}</span>
                    </div>
                    <div class="flex items-center gap-1.5">
                        <UButton
                            v-if="loading && !queued"
                            size="sm"
                            color="neutral"
                            variant="outline"
                            icon="i-heroicons-stop-circle"
                            @click="emit('stop')"
                        >
                            Stop
                        </UButton>
                        <UButton
                            size="sm"
                            color="primary"
                            :icon="queued ? 'i-heroicons-clock' : sendIcon"
                            :disabled="disabled || processingFiles || !!inputLimitError || (!allowEmptySubmit && !textModel.trim() && selectedFiles.length === 0) || queued"
                            @click="onSubmit"
                        >
                            {{ queued ? 'Queued' : loading ? 'Queue' : 'Send' }}
                        </UButton>
                    </div>
                </div>
            </template>
        </UChatPrompt>
    </div>
</template>

<script setup lang="ts">
import JSZip from 'jszip';
import { CHAT_FILE_BYTE_LIMIT, CHAT_TEXT_BYTE_LIMIT, CHAT_INPUT_LIMIT_MESSAGE, chatInputLimitError } from '@coldreach/utils/chat-input-budget';
import { chatUserText } from '~/utils/chat-user-message';

export interface ChatFilePart {
    type: 'file';
    mediaType: string;
    filename?: string;
    url: string;
}

export interface ChatAttachedFile extends ChatFilePart {
    extractedText?: string;
}

const props = withDefaults(defineProps<{
    placeholder?: string;
    disabled?: boolean;
    loading?: boolean;
    queued?: boolean;
    allowEmptySubmit?: boolean;
    rows?: number;
    maxrows?: number;
    autofocus?: boolean;
    sendIcon?: string;
    size?: 'sm' | 'md' | 'lg' | 'xl';
}>(), {
    placeholder: 'Type your message...',
    disabled: false,
    loading: false,
    queued: false,
    allowEmptySubmit: false,
    rows: 1,
    maxrows: 5,
    autofocus: true,
    sendIcon: 'i-ion-send',
    size: 'md',
});

const textModel = defineModel<string>({ default: '' });

const emit = defineEmits<{
    submit: [payload: { text: string; files: ChatAttachedFile[] }];
    stop: [];
}>();

const selectedFiles = ref<ChatAttachedFile[]>([]);
const fileError = ref('');
const processingFiles = ref(false);
const fileInputRef = ref<HTMLInputElement>();
const promptRef = ref<{ $el?: HTMLElement }>();

// --- Voice transcription ---
const { isRecording, isTranscribing, error: voiceError, liveTranscript, startRecording, stopRecording, registerStopCallback, registerKeyboardShortcut, unregisterKeyboardShortcut } = useSpeechRecognition();
let textBeforeRecording = "";

// Feature discovery — show hint dot until user first uses voice input
const { dismissed: voiceDiscovered, dismiss: markVoiceDiscovered } = useFeatureDiscovery("voice-transcription");
const isMac = ref(false);
onMounted(() => {
    isMac.value = navigator.platform?.includes("Mac") || navigator.userAgent?.includes("Mac");
});
const voiceTooltip = computed(() => {
    const shortcut = isMac.value ? "⌥V" : "Alt+V";
    return `Voice input — press ${shortcut} to start/stop, or click and hold`;
});

// Update text model in real-time as transcription comes in
watch(liveTranscript, (transcript) => {
    if (!isRecording.value && !isTranscribing.value) return;
    const prefix = textBeforeRecording.trim();
    textModel.value = prefix ? `${prefix} ${transcript}` : transcript;
});

async function onVoiceStart() {
    if (props.disabled || isTranscribing.value) return;
    markVoiceDiscovered();
    textBeforeRecording = textModel.value;
    await startRecording();
}

// Register the stop callback so document-level mouseup/touchend and keyup triggers it
registerStopCallback(() => {
    stopRecording();
    focus();
});

// Register keyboard shortcut (Alt+V hold-to-talk)
onMounted(() => {
    registerKeyboardShortcut(
        () => !props.disabled && !isTranscribing.value,
        () => {
            markVoiceDiscovered();
            textBeforeRecording = textModel.value;
        },
    );
});
onUnmounted(() => {
    unregisterKeyboardShortcut();
});

function openFilePicker() {
    fileInputRef.value?.click();
}

function removeFile(index: number) {
    selectedFiles.value = selectedFiles.value.filter((_, i) => i !== index);
}

const inputLimitError = computed(() => chatInputLimitError([
    { type: 'text', text: chatUserText(textModel.value, selectedFiles.value) },
]));

function onSubmit() {
    if (props.disabled || props.queued || processingFiles.value) return;
    if (!props.allowEmptySubmit && !textModel.value.trim() && selectedFiles.value.length === 0) return;
    if (inputLimitError.value) return;
    const files = [...selectedFiles.value];
    const text = textModel.value;
    selectedFiles.value = [];
    fileError.value = '';
    emit('submit', { text, files });
}

// Expose for parent components that need to focus the textarea
function focus() {
    const el = promptRef.value?.$el?.querySelector('textarea');
    el?.focus();
}

defineExpose({ focus, selectedFiles, addFiles });

// --- File processing ---

const OFFICE_EXTENSIONS = new Set(['.pptx', '.docx', '.xlsx']);

const ACCEPTED_EXTENSIONS = [
    '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp',
    '.pdf',
    '.tsv', '.txt', '.md', '.html', '.htm', '.json', '.xml',
    '.yaml', '.yml', '.log', '.sql', '.ini', '.toml', '.cfg', '.env',
    '.py', '.js', '.ts', '.jsx', '.tsx', '.css', '.scss', '.less',
    '.sh', '.bash', '.rb', '.go', '.rs', '.java', '.kt', '.swift',
    '.c', '.cpp', '.h', '.hpp', '.r', '.lua', '.pl', '.php',
    '.pptx', '.docx', '.xlsx',
].join(',');

function getExtension(filename: string): string {
    const dot = filename.lastIndexOf('.');
    return dot >= 0 ? filename.slice(dot).toLowerCase() : '';
}

function isNativeFile(file: File): boolean {
    return file.type.startsWith('image/') || file.type === 'application/pdf';
}

function isOfficeFile(file: File): boolean {
    return OFFICE_EXTENSIONS.has(getExtension(file.name));
}

// Stop decompression as soon as actual expanded bytes exceed the per-file budget.
// ZIP metadata alone is not a safe bound, and entry.async() buffers everything.
function readOfficeXml(entry: JSZip.JSZipObject, budget: { bytes: number }): Promise<string> {
    return new Promise((resolve, reject) => {
        const chunks: Uint8Array[] = [];
        // JSZip 3.10 exposes this streaming API at runtime but omits it from JSZipObject's types.
        const stream = (entry as JSZip.JSZipObject & {
            internalStream(type: 'uint8array'): JSZip.JSZipStreamHelper<Uint8Array>;
        }).internalStream('uint8array');
        stream.on('data', chunk => {
            budget.bytes += chunk.byteLength;
            if (budget.bytes > CHAT_FILE_BYTE_LIMIT) {
                stream.pause();
                reject(new Error('This Office file expands beyond the 10 MiB chat limit. Use Import contacts/companies for a full list.'));
                return;
            }
            chunks.push(chunk);
        });
        stream.on('error', reject);
        stream.on('end', () => {
            const decoder = new TextDecoder();
            resolve(chunks.map(chunk => decoder.decode(chunk, { stream: true })).join('') + decoder.decode());
        });
        stream.resume();
    });
}

function addExtractedBytes(text: string, budget: { bytes: number }) {
    budget.bytes += new TextEncoder().encode(text).byteLength;
    if (budget.bytes > CHAT_TEXT_BYTE_LIMIT) throw new Error(CHAT_INPUT_LIMIT_MESSAGE);
}

async function extractTextFromPptx(buffer: ArrayBuffer): Promise<string> {
    const zip = await JSZip.loadAsync(buffer);
    const xmlBudget = { bytes: 0 };
    const textBudget = { bytes: 0 };
    const slides: { num: number; text: string }[] = [];
    for (const [path, entry] of Object.entries(zip.files)) {
        const match = path.match(/^ppt\/slides\/slide(\d+)\.xml$/);
        if (!match) continue;
        const xml = await readOfficeXml(entry, xmlBudget);
        const doc = new DOMParser().parseFromString(xml, 'application/xml');
        const textNodes = doc.getElementsByTagName('a:t');
        const texts: string[] = [];
        for (let i = 0; i < textNodes.length; i++) {
            const t = textNodes[i].textContent;
            if (t) {
                addExtractedBytes(t + " ", textBudget);
                texts.push(t);
            }
        }
        slides.push({ num: parseInt(match[1]), text: texts.join(' ') });
    }
    slides.sort((a, b) => a.num - b.num);
    return slides.map(s => `[Slide ${s.num}]\n${s.text}`).join('\n\n');
}

async function extractTextFromDocx(buffer: ArrayBuffer): Promise<string> {
    const zip = await JSZip.loadAsync(buffer);
    const xmlBudget = { bytes: 0 };
    const textBudget = { bytes: 0 };
    const docFile = zip.file('word/document.xml');
    if (!docFile) return '[Could not read document content]';
    const xml = await readOfficeXml(docFile, xmlBudget);
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    const textNodes = doc.getElementsByTagName('w:t');
    const paragraphs: string[] = [];
    let current = '';
    for (let i = 0; i < textNodes.length; i++) {
        const node = textNodes[i];
        const t = node.textContent || '';
        addExtractedBytes(t + '\n', textBudget);
        current += t;
        const wp = node.closest?.('w\\:p') || node.parentElement?.closest?.('w\\:p');
        const nextNode = textNodes[i + 1];
        const nextWp = nextNode?.closest?.('w\\:p') || nextNode?.parentElement?.closest?.('w\\:p');
        if (!nextNode || wp !== nextWp) {
            if (current.trim()) paragraphs.push(current.trim());
            current = '';
        }
    }
    return paragraphs.join('\n');
}

async function extractTextFromXlsx(buffer: ArrayBuffer): Promise<string> {
    const zip = await JSZip.loadAsync(buffer);
    const xmlBudget = { bytes: 0 };
    const textBudget = { bytes: 0 };
    const ssFile = zip.file('xl/sharedStrings.xml');
    const sharedStrings: string[] = [];
    if (ssFile) {
        const xml = await readOfficeXml(ssFile, xmlBudget);
        const doc = new DOMParser().parseFromString(xml, 'application/xml');
        const siNodes = doc.getElementsByTagName('si');
        for (let i = 0; i < siNodes.length; i++) {
            const tNodes = siNodes[i].getElementsByTagName('t');
            const parts: string[] = [];
            for (let j = 0; j < tNodes.length; j++) {
                parts.push(tNodes[j].textContent || '');
            }
            sharedStrings.push(parts.join(''));
        }
    }
    const sheets: string[] = [];
    for (const [path, entry] of Object.entries(zip.files)) {
        const match = path.match(/^xl\/worksheets\/sheet(\d+)\.xml$/);
        if (!match) continue;
        const xml = await readOfficeXml(entry, xmlBudget);
        const doc = new DOMParser().parseFromString(xml, 'application/xml');
        const rows = doc.getElementsByTagName('row');
        const rowTexts: string[] = [];
        for (let r = 0; r < rows.length; r++) {
            const cells = rows[r].getElementsByTagName('c');
            const cellValues: string[] = [];
            for (let c = 0; c < cells.length; c++) {
                const cell = cells[c];
                const vNode = cell.getElementsByTagName('v')[0];
                const val = vNode?.textContent || '';
                const cellText = cell.getAttribute('t') === 's' ? (sharedStrings[parseInt(val)] ?? '') : val;
                addExtractedBytes(cellText + '\t', textBudget);
                if (cell.getAttribute('t') === 's' && sharedStrings[parseInt(val)]) {
                    cellValues.push(sharedStrings[parseInt(val)]);
                } else {
                    cellValues.push(val);
                }
            }
            rowTexts.push(cellValues.join('\t'));
        }
        sheets.push(`[Sheet ${match[1]}]\n${rowTexts.join('\n')}`);
    }
    return sheets.join('\n\n');
}

async function onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    await addFiles(Array.from(input.files));
    input.value = '';
}

async function addFiles(files: File[]) {
    if (props.disabled || processingFiles.value) return;
    processingFiles.value = true;
    fileError.value = '';
    const newFiles: ChatAttachedFile[] = [];
    try {
        for (const file of files) {
            if (file.size > CHAT_FILE_BYTE_LIMIT) {
                throw new Error(`File "${file.name}" exceeds the 10 MiB limit.`);
            }
            let attached: ChatAttachedFile;
            if (isNativeFile(file)) {
                const dataUrl = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
                attached = { type: 'file', mediaType: file.type, filename: file.name, url: dataUrl };
            } else {
                let extractedText: string;
                if (isOfficeFile(file)) {
                    const buffer = await file.arrayBuffer();
                    const ext = getExtension(file.name);
                    if (ext === '.pptx') extractedText = await extractTextFromPptx(buffer);
                    else if (ext === '.docx') extractedText = await extractTextFromDocx(buffer);
                    else extractedText = await extractTextFromXlsx(buffer);
                } else {
                    if (file.size > CHAT_TEXT_BYTE_LIMIT) throw new Error(CHAT_INPUT_LIMIT_MESSAGE);
                    extractedText = await file.text();
                }
                attached = { type: 'file', mediaType: file.type, filename: file.name, url: '', extractedText };
            }
            newFiles.push(attached);
            const limitError = chatInputLimitError([{
                type: 'text', text: chatUserText(textModel.value, [...selectedFiles.value, ...newFiles]),
            }]);
            if (limitError) throw new Error(limitError);
        }
        selectedFiles.value = [...selectedFiles.value, ...newFiles];
    } catch (error) {
        fileError.value = error instanceof Error ? error.message : 'Could not read this file. Try an unprotected document or a text file.';
    } finally {
        processingFiles.value = false;
    }
}

</script>
